import {
  computeWaitingOnReviewers,
  type SubmissionForWaitingOn,
  type SubmissionReviewerForWaitingOn,
} from "./computeWaitingOnUsers";
import { isStaffUserType } from "./staffDisplay";

const DAY_MS = 24 * 60 * 60 * 1000;

type ReviewerForWaitingSince<TUser> = SubmissionReviewerForWaitingOn<TUser> & {
  id: string;
};

export type SubmissionForWaitingSince<TUser> = SubmissionForWaitingOn<TUser> & {
  reviewers: ReviewerForWaitingSince<TUser>[];
};

/** Current customer waits, by user id. Missing readiness history stays undated. */
export function computeCustomerWaits<TUser extends { id: string; type: string }>(
  submission: SubmissionForWaitingSince<TUser>,
  readyAtByReviewer: ReadonlyMap<string, Date>
): Map<string, Date> {
  const waits = new Map<string, Date>();

  for (const reviewer of computeWaitingOnReviewers(submission)) {
    if (isStaffUserType(reviewer.user.type)) {
      continue;
    }
    const since = readyAtByReviewer.get(reviewer.id);
    if (since) waits.set(reviewer.user.id, since);
  }
  return waits;
}

/** Whole days since `since`; zero until a full day has passed. */
export function daysWaiting(since: Date, now: Date = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - since.getTime()) / DAY_MS));
}

/** {@link daysWaiting} for the longest of several waits; null when none is dated. */
export function longestDaysWaiting(
  waitingSinces: ReadonlyArray<Date | null>,
  now: Date = new Date()
): number | null {
  const dated = waitingSinces.flatMap((since) => (since ? [since.getTime()] : []));
  return dated.length > 0 ? daysWaiting(new Date(Math.min(...dated)), now) : null;
}
